import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Flex, Text } from 'rebass';
import { Checkbox, Textarea } from '@rebass/forms';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import Button from '../Button/Button';
import useNotify from '../../hooks/useNotify';
import { UPDATE_HAZARD_SURVEY_STATUS } from './queries';
import { HazardSurveyStatus } from '../../services/hazardSurvey';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';

type SurveyStatusModalProps = {
  jobId: string;
  newStatus: HazardSurveyStatus;
  onClose: (actionTaken?: boolean) => void;
};

const statusLabels: Record<HazardSurveyStatus, string> = {
  NEW: 'New',
  IN_PROGRESS: 'In Progress',
  IN_REVIEW: 'In Review',
  ACCEPTED: 'Accept',
  CANCELLED: 'Cancel',
};

const SurveyStatusModal: React.FC<SurveyStatusModalProps> = ({
  jobId,
  newStatus,
  onClose,
}) => {
  const { showError } = useNotify();
  const [comment, setComment] = useState('');
  const [updateWaterService, setUpdateWaterService] = useState(true);

  const [updateStatus, { loading }] = useMutation(UPDATE_HAZARD_SURVEY_STATUS, {
    onCompleted: () =>
      onClose(newStatus === 'ACCEPTED' ? updateWaterService : true),
    onError: useCallback((error: any) => {
      showError('Error updating survey status', error);
    }, []),
  });

  const handleConfirm = useCallback(() => {
    updateStatus({
      variables: {
        jobId,
        status: newStatus,
        comment: comment || undefined,
        updateWaterService:
          newStatus === 'ACCEPTED' ? updateWaterService : undefined,
      },
    });
  }, [updateStatus, jobId, newStatus, comment, updateWaterService]);

  const title =
    newStatus === 'CANCELLED'
      ? 'Cancel Survey'
      : newStatus === 'ACCEPTED'
        ? 'Accept Survey'
        : `Set Survey to ${statusLabels[newStatus] ?? constantToLabel(newStatus)}`;

  return (
    <Modal title={title} onClose={() => onClose()}>
      <ModalBody>
        <Flex p={3} flexDirection="column" sx={{ gap: 3 }}>
          <Textarea
            height={3 * 16 + 2}
            placeholder="Optional comment"
            value={comment}
            onChange={(e: any) => setComment(e.target.value)}
          />
          {newStatus === 'ACCEPTED' && (
            <Flex alignItems="center" sx={{ gap: 2 }}>
              <Checkbox
                id="updateWaterService"
                checked={updateWaterService}
                onChange={(e: any) => setUpdateWaterService(e.target.checked)}
              />
              <Text
                as="label"
                htmlFor="updateWaterService"
                fontSize={1}
                sx={{ cursor: 'pointer' }}
              >
                Update water service hazards with survey results
              </Text>
            </Flex>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button icon="check-circle" disabled={loading} onClick={handleConfirm}>
          Confirm
        </Button>
        <Button
          ml={3}
          variant="outline"
          icon="slash-circle"
          onClick={() => onClose()}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SurveyStatusModal;
